import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Plus, X, Save, Mail, Users, Settings2 } from "lucide-react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [newRecipient, setNewRecipient] = useState("");
  const [newCc, setNewCc] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const addRecipient = () => {
    const v = newRecipient.trim();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error("Indirizzo email non valido");
      return;
    }
    if (s.recipients.includes(v)) return;
    setS((p) => ({ ...p, recipients: [...p.recipients, v] }));
    setNewRecipient("");
  };

  const removeRecipient = (email) =>
    setS((p) => ({ ...p, recipients: p.recipients.filter((r) => r !== email) }));

  const addCc = () => {
    const v = newCc.trim();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error("Indirizzo email non valido");
      return;
    }
    if ((s.cc_recipients || []).includes(v)) return;
    setS((p) => ({ ...p, cc_recipients: [...(p.cc_recipients || []), v] }));
    setNewCc("");
  };

  const removeCc = (email) =>
    setS((p) => ({ ...p, cc_recipients: (p.cc_recipients || []).filter((r) => r !== email) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        recipients: s.recipients,
        cc_recipients: s.cc_recipients || [],
        email_subject: s.email_subject,
        email_body: s.email_body,
        mandante: s.mandante,
        tipo_ordine: s.tipo_ordine,
        causale: s.causale,
        magazzino: s.magazzino,
      };
      await api.put("/settings", payload);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (!s) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <Tabs defaultValue="recipients">
        <TabsList className="mb-6">
          <TabsTrigger value="recipients" data-testid="tab-recipients" className="gap-2">
            <Users className="h-4 w-4" /> Destinatari
          </TabsTrigger>
          <TabsTrigger value="template" data-testid="tab-template" className="gap-2">
            <Mail className="h-4 w-4" /> Modello Email
          </TabsTrigger>
          <TabsTrigger value="params" data-testid="tab-params" className="gap-2">
            <Settings2 className="h-4 w-4" /> Parametri MM
          </TabsTrigger>
        </TabsList>

        <TabsContent value="recipients">
          <Card title="Destinatari fissi" desc="Condivisi tra tutti gli utenti. Indirizzi a cui verrà inviato il file ordine (campo A: destinatari principali).">
            <div className="flex gap-2">
              <Input
                data-testid="new-recipient-input"
                value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addRecipient()}
                placeholder="destinatario@azienda.com"
              />
              <Button data-testid="add-email-recipient-button" onClick={addRecipient} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4" /> Aggiungi
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {s.recipients.length === 0 && <p className="text-sm text-slate-400">Nessun destinatario configurato.</p>}
              {s.recipients.map((r) => (
                <span
                  key={r}
                  data-testid={`recipient-chip-${r}`}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 px-3 py-1.5 text-sm font-medium"
                >
                  {r}
                  <button onClick={() => removeRecipient(r)} className="hover:text-rose-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          </Card>

          <div className="mt-6">
            <Card title="Destinatari in CC" desc="CC di default (condiviso). Verrà precompilato all'invio e potrai comunque modificarlo volta per volta.">
              <div className="flex gap-2">
                <Input
                  data-testid="new-cc-input"
                  value={newCc}
                  onChange={(e) => setNewCc(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addCc()}
                  placeholder="copia@azienda.com"
                />
                <Button data-testid="add-cc-recipient-button" onClick={addCc} variant="outline" className="gap-1.5">
                  <Plus className="h-4 w-4" /> Aggiungi
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {(s.cc_recipients || []).length === 0 && <p className="text-sm text-slate-400">Nessun CC configurato.</p>}
                {(s.cc_recipients || []).map((r) => (
                  <span
                    key={r}
                    data-testid={`cc-chip-${r}`}
                    className="inline-flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-3 py-1.5 text-sm font-medium"
                  >
                    {r}
                    <button onClick={() => removeCc(r)} className="hover:text-rose-600">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="template">
          <Card title="Modello Email" desc="Segnaposto: {documenti}, {num_documenti}, {delivery_note_id}, {cliente}, {nazione}, {mandante}.">
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-xs text-slate-500 space-y-0.5">
                <div><span className="font-mono-data text-slate-700 dark:text-slate-300">{"{documenti}"}</span> = elenco documenti SAP (numero — cliente — nazione), uno per riga &middot; <span className="font-mono-data text-slate-700 dark:text-slate-300">{"{num_documenti}"}</span> = numero consegne</div>
                <div><span className="font-mono-data text-slate-700 dark:text-slate-300">{"{cliente}"}</span>, <span className="font-mono-data text-slate-700 dark:text-slate-300">{"{nazione}"}</span>, <span className="font-mono-data text-slate-700 dark:text-slate-300">{"{delivery_note_id}"}</span> = riferiti alla PRIMA consegna della run</div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Oggetto</Label>
                <Input
                  data-testid="email-subject-input"
                  value={s.email_subject}
                  onChange={(e) => setS((p) => ({ ...p, email_subject: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Corpo del messaggio</Label>
                <Textarea
                  data-testid="email-body-input"
                  rows={6}
                  value={s.email_body}
                  onChange={(e) => setS((p) => ({ ...p, email_body: e.target.value }))}
                />
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="params">
          <Card title="Parametri fissi MM Operations" desc="Valori costanti scritti nelle colonne A, C, D, E del file.">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { key: "mandante", label: "Mandante (A)" },
                { key: "tipo_ordine", label: "Tipo Ordine (C)" },
                { key: "causale", label: "Causale (D)" },
                { key: "magazzino", label: "Magazzino (E)" },
              ].map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label className="text-xs text-slate-500">{f.label}</Label>
                  <Input
                    data-testid={`param-${f.key}-input`}
                    value={s[f.key]}
                    onChange={(e) => setS((p) => ({ ...p, [f.key]: e.target.value }))}
                    className="font-mono-data"
                  />
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-6 flex justify-end">
        <Button data-testid="save-settings-button" onClick={save} disabled={saving} className="gap-2 bg-blue-600 hover:bg-blue-700">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva impostazioni
        </Button>
      </div>
    </div>
  );
}

const Card = ({ title, desc, children }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
    <h3 className="font-display font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
    <p className="text-sm text-slate-500 mt-1 mb-5">{desc}</p>
    {children}
  </div>
);
